<template>
  <div class="container-fluid">
    <h1>Контроль дублирования контингента ДО</h1>
    <p class="text-muted">Дети, табелируемые в двух и более детских садах с пересекающимися периодами пребывания за отчётный месяц (плановые табели).</p>

    <div class="row mb-2">
      <div class="col-md-1">
        <label class="form-label">Месяц</label>
        <input type="number" min="1" max="12" class="form-control" v-model.number="params.accountingMonth">
      </div>
      <div class="col-md-1">
        <label class="form-label">Год</label>
        <input type="number" min="2024" class="form-control" v-model.number="params.accountingYear">
      </div>
      <div class="col-md-3">
        <label class="form-label">Населённый пункт</label>
        <multiselect v-model="kato" :options="katoList" :searchable="true" :closeOnSelect="true" track-by="code"
                     :custom-label="formattedKatoLabel" placeholder="Все" :multiple="false"/>
      </div>
      <div class="col-md-2">
        <label class="form-label">LMS-система</label>
        <input class="form-control" v-model="params.lmsName" placeholder="INDIGO, SmartNation…">
      </div>
      <div class="col-md-2">
        <label class="form-label">Статус проверки</label>
        <select class="form-control" v-model="params.status">
          <option :value="null">Все</option>
          <option v-for="(label, value) in statusLabels" :key="value" :value="value">{{ label }}</option>
        </select>
      </div>
      <div class="col-md-3">
        <label class="form-label">ИИН / паспорт ребёнка</label>
        <input class="form-control" v-model="params.childKey">
      </div>
    </div>
    <div class="mb-3">
      <button class="btn btn-primary" @click="fetch(true)" :disabled="loading || !params.accountingMonth || !params.accountingYear">{{ loading ? "Загрузка..." : "Поиск" }}</button>
      <button class="btn btn-outline-secondary ms-2" @click="fetch(true, true)" :disabled="loading" title="Результат поиска хранится 5 минут; пересчитать по свежим табелям">Пересчитать</button>
      <button class="btn btn-warning ms-2" @click="resetFilters">Сбросить фильтры</button>
      <button class="btn btn-info ms-2" @click="exportXlsx" :disabled="exporting || total === 0">{{ exporting ? "Формируется..." : "Выгрузить в Excel" }}</button>
    </div>

    <div v-if="summary" class="row mb-3 summary">
      <div class="col-md-3"><b>Детей-дублей:</b> {{ summary.children }}</div>
      <div class="col-md-4"><b>Сумма двойного начисления:</b> {{ money(summary.totalAmount) }} ₸</div>
      <div class="col-md-3"><b>Ожидает решения:</b> {{ summary.pending }}</div>
    </div>

    <b-pagination v-model="currentPage" :total-rows="total" :per-page="params.pageSize"></b-pagination>

    <el-table :data="rows" style="width: 100%" border :row-class-name="rowClassName">
      <el-table-column label="№" width="60">
        <template #default="scope">{{ (currentPage - 1) * params.pageSize + scope.$index + 1 }}</template>
      </el-table-column>
      <el-table-column prop="childKey" label="ИИН ребёнка" width="140"></el-table-column>
      <el-table-column prop="fio" label="ФИО ребёнка" min-width="180"></el-table-column>
      <el-table-column v-for="side in sides" :key="side.key" :label="side.label" align="center">
        <el-table-column label="Наименование" min-width="180">
          <template #default="scope">{{ scope.row[side.key].name }}</template>
        </el-table-column>
        <el-table-column label="БИН" width="130">
          <template #default="scope">{{ scope.row[side.key].bin }}</template>
        </el-table-column>
        <el-table-column label="LMS ID" width="110">
          <template #default="scope">{{ scope.row[side.key].lmsId }}</template>
        </el-table-column>
        <el-table-column label="LMS Система" width="120">
          <template #default="scope">{{ scope.row[side.key].lmsName }}</template>
        </el-table-column>
        <el-table-column label="Сумма начисления" width="130" align="right">
          <template #default="scope">
            <span v-if="scope.row[side.key].amount != null">{{ money(scope.row[side.key].amount) }}</span>
            <small v-else class="text-muted">расчёт не выполнен</small>
          </template>
        </el-table-column>
      </el-table-column>
      <el-table-column label="Итого" width="120" align="right">
        <template #default="scope">
          <b>{{ money(scope.row.totalAmount) }}</b>
          <div v-if="scope.row.kindergartenCount > 2"><small class="text-muted">всего садиков: {{ scope.row.kindergartenCount }}</small></div>
          <div><small class="text-muted">пересечение: {{ scope.row.overlapDays }} дн.</small></div>
        </template>
      </el-table-column>
      <el-table-column label="Статус проверки" width="230">
        <template #default="scope">
          <select class="form-control form-control-sm" :value="scope.row.status" :disabled="!canEdit" @change="onStatusPicked(scope.row, $event)">
            <option v-for="(label, value) in statusLabels" :key="value" :value="value">{{ label }}</option>
          </select>
          <small v-if="scope.row.reviewedAt" class="text-muted d-block">{{ formatDate(scope.row.reviewedAt) }}<span v-if="scope.row.reviewedByName"> · {{ scope.row.reviewedByName }}</span><span v-if="scope.row.comment">: {{ scope.row.comment }}</span></small>
          <small v-if="scope.row.stale" class="text-danger d-block">состав садиков изменился после решения</small>
        </template>
      </el-table-column>
    </el-table>

    <DeleteReasonDialog v-model="showStatusDialog" variant="restore" title="Изменить статус проверки" confirm-text="Сохранить"
                        reason-label="Комментарий" :loading="saving" @confirm="confirmStatus" @update:model-value="onDialogToggle">
      <p v-if="pending">
        <b>{{ pending.row.fio }}</b> ({{ pending.row.childKey }}) → <b>{{ statusLabels[pending.status] }}</b>
      </p>
    </DeleteReasonDialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, watch } from "vue";
import { ElMessage } from "element-plus";
import Multiselect from "vue-multiselect";
import DeleteReasonDialog from "@/components/DeleteReasonDialog.vue";
import { extractApiError } from "@/utils/apiError";
import cacheService from "@/services/cacheService";
import authService from "@/services/authService";
import { Kato } from "@/types/kindergarten";
import {
  changeDuplicateStatus, ChildDuplicateRow, ChildDuplicateSearchParams, ChildDuplicateStatus, exportDuplicates, findDuplicates, STATUS_LABELS,
} from "@/services/duplicatesService";

export default defineComponent({
  name: "ChildDuplicatesView",
  components: { Multiselect, DeleteReasonDialog },
  setup() {
    const previousMonth = () => {
      const d = new Date();
      d.setDate(1);
      d.setMonth(d.getMonth() - 1);
      return { accountingMonth: d.getMonth() + 1, accountingYear: d.getFullYear() };
    };
    const defaultParams = (): ChildDuplicateSearchParams => ({ page: 0, pageSize: 50, ...previousMonth(), katoCode: null, lmsName: null, status: null, childKey: null });

    const params = ref<ChildDuplicateSearchParams>(defaultParams());
    const kato = ref<Kato | null>(null);
    const katoList = ref<Kato[]>([]);
    const rows = ref<ChildDuplicateRow[]>([]);
    const summary = ref<{ children: number; totalAmount: number; pending: number } | null>(null);
    const total = ref(0);
    const loading = ref(false);
    const exporting = ref(false);
    const saving = ref(false);
    const currentPage = ref(1);
    const sides = [{ key: "kg1", label: "Детский сад №1" }, { key: "kg2", label: "Детский сад №2" }];
    const canEdit = authService.hasAccess([...authService.getManagerRoles(), ...authService.getAdminWriteRoles()]);

    const showStatusDialog = ref(false);
    const pending = ref<{ row: ChildDuplicateRow; status: ChildDuplicateStatus } | null>(null);

    const requestParams = (): ChildDuplicateSearchParams => ({
      ...params.value,
      page: currentPage.value - 1,
      katoCode: kato.value?.code ?? null,
      lmsName: params.value.lmsName?.trim() || null,
      childKey: params.value.childKey?.trim() || null,
    });

    const fetch = async (resetPage = false, refresh = false) => {
      if (resetPage) currentPage.value = 1;
      loading.value = true;
      try {
        const page = await findDuplicates({ ...requestParams(), refresh });
        rows.value = page.content;
        total.value = page.totalElements;
        summary.value = page.summary;
      } catch (error) {
        ElMessage({ message: extractApiError(error, "Не удалось загрузить дубли"), type: "error", showClose: true });
      } finally {
        loading.value = false;
      }
    };

    const resetFilters = () => {
      params.value = defaultParams();
      kato.value = null;
      fetch(true);
    };

    const exportXlsx = async () => {
      exporting.value = true;
      try {
        await exportDuplicates(requestParams());
      } catch (error) {
        ElMessage({ message: extractApiError(error, "Не удалось сформировать Excel"), type: "error", showClose: true });
      } finally {
        exporting.value = false;
      }
    };

    const onStatusPicked = (row: ChildDuplicateRow, event: Event) => {
      const status = (event.target as HTMLSelectElement).value as ChildDuplicateStatus;
      if (status === row.status) return;
      pending.value = { row, status };
      showStatusDialog.value = true;
    };

    /** Отмена диалога — вернуть селект к текущему статусу строки (селект не привязан v-model, поэтому просто перерисовать). */
    const onDialogToggle = (visible: boolean) => {
      if (!visible && pending.value) {
        const row = pending.value.row;
        rows.value = rows.value.map((r) => (r === row ? { ...r } : r));
        pending.value = null;
      }
    };

    const confirmStatus = async (comment: string) => {
      if (!pending.value) return;
      const { row, status } = pending.value;
      saving.value = true;
      try {
        const updated = await changeDuplicateStatus(row, params.value.accountingYear, params.value.accountingMonth, status, comment);
        rows.value = rows.value.map((r) => (r === row ? updated : r));
        if (summary.value) {
          const delta = (row.status === "PENDING" ? -1 : 0) + (updated.status === "PENDING" ? 1 : 0);
          summary.value = { ...summary.value, pending: summary.value.pending + delta };
        }
        pending.value = null;
        showStatusDialog.value = false;
      } catch (error) {
        ElMessage({ message: extractApiError(error, "Не удалось сохранить статус"), type: "error", showClose: true });
      } finally {
        saving.value = false;
      }
    };

    const money = (value: number | null) => (value == null ? "" : Number(value).toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    const formatDate = (iso: string) => new Date(iso).toLocaleString("ru-RU");
    const formattedKatoLabel = (item: Kato) => `${item.code} | ${item.nameRu}`;
    const rowClassName = ({ row }: { row: ChildDuplicateRow }) => (row.status === "CONFIRMED_DUPLICATE" ? "confirmed-row" : row.status === "FALSE_DUPLICATE" ? "false-row" : "");

    watch(currentPage, () => fetch(false));
    onMounted(async () => {
      katoList.value = await cacheService.useKatoStore().fetchKato();
      await fetch(true);
    });

    return {
      params, kato, katoList, rows, summary, total, loading, exporting, saving, currentPage, sides, canEdit,
      statusLabels: STATUS_LABELS, showStatusDialog, pending,
      fetch, resetFilters, exportXlsx, onStatusPicked, onDialogToggle, confirmStatus, money, formatDate, formattedKatoLabel, rowClassName,
    };
  },
});
</script>

<style scoped>
.summary { font-size: 15px; }
</style>
<style>
.el-table .confirmed-row { background-color: #fdecea; }
.el-table .false-row { background-color: #eef7ee; }
</style>
